import { useState } from "react";
import { FlaskConical, Settings2 } from "lucide-react";
import type { ChannelOverride } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/lib/hooks/useTenant";
import { useEntitlements } from "@/lib/entitlements";
import {
  useChannelOverrides,
  useForwardingChannels,
  useForwardingRoutes,
  useForwardingSettings,
  type ForwardingChannel,
} from "@/lib/hooks/useForwarding";
import { useTextTemplates } from "@/lib/hooks/useTextTemplates";
import { ModeRoutesCard } from "./ModeRoutesCard";
import { WindowCard } from "./WindowCard";
import { ChannelList } from "./ChannelList";
import { GlobalDefaultsSheet } from "./GlobalDefaultsSheet";
import { ChannelOverrideSheet } from "./ChannelOverrideSheet";
import { FilterSimulator } from "./FilterSimulator";
import { ForwardingHealth } from "./ForwardingHealth";
import { InlineError } from "./controls";

function findOverride(
  overrides: readonly ChannelOverride[],
  channel: ForwardingChannel | null,
): ChannelOverride | null {
  if (!channel) return null;
  return (
    overrides.find((o) => o.platform === channel.platform && o.channelRef === channel.ref) ?? null
  );
}

function useForwardingData() {
  const settings = useForwardingSettings();
  const channels = useForwardingChannels();
  const routes = useForwardingRoutes();
  const overrides = useChannelOverrides();
  const templates = useTextTemplates();
  const error =
    settings.error ?? channels.error ?? routes.error ?? overrides.error ?? templates.error;
  return {
    settings: settings.data,
    channels: channels.data ?? [],
    routes: routes.data ?? [],
    overrides: overrides.data ?? [],
    templates: templates.data ?? [],
    isLoading: settings.isLoading || channels.isLoading,
    error,
  };
}

type ForwardingData = ReturnType<typeof useForwardingData>;

function LoadingState({ error }: { error: ForwardingData["error"] }): JSX.Element {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Carregando encaminhamento">
      {error ? (
        <InlineError message="Não foi possível carregar o encaminhamento. Tente recarregar a página." />
      ) : null}
      {!error &&
        [0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.4)]"
          />
        ))}
    </div>
  );
}

function ActionsBar({
  onGlobal,
  onSimulator,
}: {
  onGlobal: () => void;
  onSimulator: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={onGlobal}>
        <Settings2 size={15} />
        Padrão global
      </Button>
      <Button variant="outline" onClick={onSimulator}>
        <FlaskConical size={15} />
        Simular filtros
      </Button>
    </div>
  );
}

function ForwardingConfig({
  data,
  settings,
}: {
  data: ForwardingData;
  settings: NonNullable<ForwardingData["settings"]>;
}): JSX.Element {
  const { data: billing } = useTenant();
  const entitlements = useEntitlements(billing?.tenant);
  const [globalOpen, setGlobalOpen] = useState(false);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [overrideChannel, setOverrideChannel] = useState<ForwardingChannel | null>(null);

  return (
    <div className="space-y-5">
      {data.error && (
        <InlineError message="Parte dos dados não carregou. Algumas listas podem estar incompletas." />
      )}
      <ActionsBar onGlobal={() => setGlobalOpen(true)} onSimulator={() => setSimulatorOpen(true)} />
      <div className="grid gap-5 xl:grid-cols-2">
        <ModeRoutesCard settings={settings} channels={data.channels} routes={data.routes} />
        <WindowCard settings={settings} />
      </div>
      <ChannelList
        channels={data.channels}
        overrides={data.overrides}
        maxDestinations={entitlements.maxForwardingDestinations}
        onOpenOverride={setOverrideChannel}
      />
      <GlobalDefaultsSheet
        open={globalOpen}
        settings={settings}
        templates={data.templates}
        onClose={() => setGlobalOpen(false)}
      />
      <FilterSimulator
        open={simulatorOpen}
        settings={settings}
        sources={data.channels.filter((c) => c.role === "origem")}
        overrides={data.overrides}
        onClose={() => setSimulatorOpen(false)}
      />
      <ChannelOverrideSheet
        channel={overrideChannel}
        override={findOverride(data.overrides, overrideChannel)}
        onClose={() => setOverrideChannel(null)}
      />
    </div>
  );
}

/** Aba de encaminhamento da tela Grupos e canais. */
export function ForwardingTab({ view }: { readonly view: "config" | "health" }): JSX.Element {
  const data = useForwardingData();
  if (data.isLoading || !data.settings) return <LoadingState error={data.error} />;
  if (view === "health") {
    return (
      <ForwardingHealth settings={data.settings} channels={data.channels} routes={data.routes} />
    );
  }
  return <ForwardingConfig data={data} settings={data.settings} />;
}
